/**
 * LibraryPage — past quotations / proposals / cover letters, tagged for
 * retrieval-augmented generation (PRD §6 Library noun, §8.5).
 *
 * Three tabs:
 *   1. Quotations    — wraps QuotationsArchivePage (existing).
 *   2. Proposals     — LibraryProposal CRUD via /api/v2/library/proposals.
 *   3. Cover letters — LibraryCoverLetter CRUD via /api/v2/library/cover-letters.
 *
 * Phase 2 (post-Simplification) will add a text-extraction worker that
 * indexes pages for FTS + embedding so the Tender workspace's Proposal &
 * Cover tab can do retrieval-augmented drafting.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { downloadFile } from "@/lib/downloadFile";
import {
  FileText, Upload, Trash2, Download, Plus, Search,
} from "lucide-react";

import { v2Api } from "@/lib/v2Api";
import { confirm } from "@/lib/confirm";
import AskWidget from "@/components/v2/AskWidget";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import QuotationsArchivePage from "@/pages/v2/QuotationsArchivePage";


// ---------------------------------------------------------------------------
// Proposal upload dialog
// ---------------------------------------------------------------------------

function ProposalUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "", client_name: "", project_name: "", proposal_date: "",
    won: "", industry: "", sector: "", product_categories: "", tags: "",
    description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setForm({
        title: "", client_name: "", project_name: "", proposal_date: "",
        won: "", industry: "", sector: "", product_categories: "", tags: "",
        description: "",
      });
    }
  }, [open]);

  const submit = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await v2Api.library.uploadProposal(file, form);
      toast.success("Uploaded");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Upload past proposal</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm py-2">
          <p className="text-xs text-slate-500">
            PDF / DOCX. Industry + sector + tags + description drive retrieval
            for the Tender workspace's Proposal & Cover tab.
          </p>
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Title *</label>
              <Input value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. CEMS supply for IOCL Mathura refinery" />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Client</label>
              <Input value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Project</label>
              <Input value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Date (yyyy-mm-dd)</label>
              <Input type="date" value={form.proposal_date}
                onChange={(e) => setForm({ ...form, proposal_date: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Outcome</label>
              <select value={form.won}
                onChange={(e) => setForm({ ...form, won: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-md bg-white text-sm">
                <option value="">Unknown</option>
                <option value="true">Won</option>
                <option value="false">Lost</option>
              </select>
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Industry</label>
              <Input value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                placeholder="Oil & Gas" />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Sector</label>
              <Input value={form.sector}
                onChange={(e) => setForm({ ...form, sector: e.target.value })}
                placeholder="CEMS / WQM / CAAQMS" />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Product categories (; separated)</label>
              <Input value={form.product_categories}
                onChange={(e) => setForm({ ...form, product_categories: e.target.value })}
                placeholder="CEMS; PMU" />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Tags (; separated)</label>
              <Input value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="containerised; OEM-authorised; 5-year-warranty" />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Description / notes</label>
              <Input value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="One-line summary that helps retrieval" />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">File *</label>
              <Input type="file" accept=".pdf,.docx,.doc,.xlsx,.xls,.txt,.md"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Cover-letter upload dialog
// ---------------------------------------------------------------------------

const COVER_TONES = ["", "formal", "warm", "technical", "govt"];

function CoverLetterUploadDialog({ open, onOpenChange, onDone }) {
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({
    title: "", client_name: "", project_name: "", letter_date: "",
    industry: "", sector: "", tone: "", tags: "", description: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setFile(null);
      setForm({
        title: "", client_name: "", project_name: "", letter_date: "",
        industry: "", sector: "", tone: "", tags: "", description: "",
      });
    }
  }, [open]);

  const submit = async () => {
    if (!file) { toast.error("Pick a file"); return; }
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      await v2Api.library.uploadCoverLetter(file, form);
      toast.success("Uploaded");
      onOpenChange(false);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail ?? "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Upload past cover letter</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm py-2">
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Title *</label>
              <Input value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Cover letter — JJM 2024 (formal)" />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Client</label>
              <Input value={form.client_name}
                onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Project</label>
              <Input value={form.project_name}
                onChange={(e) => setForm({ ...form, project_name: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Date</label>
              <Input type="date" value={form.letter_date}
                onChange={(e) => setForm({ ...form, letter_date: e.target.value })} />
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Tone</label>
              <select value={form.tone}
                onChange={(e) => setForm({ ...form, tone: e.target.value })}
                className="w-full px-2 py-1.5 border rounded-md bg-white text-sm">
                {COVER_TONES.map((t) => (
                  <option key={t} value={t}>{t || "—"}</option>
                ))}
              </select>
            </div>
            <div className="col-span-4">
              <label className="text-xs text-slate-500">Industry</label>
              <Input value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })} />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Sector</label>
              <Input value={form.sector}
                onChange={(e) => setForm({ ...form, sector: e.target.value })} />
            </div>
            <div className="col-span-6">
              <label className="text-xs text-slate-500">Tags (; separated)</label>
              <Input value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })} />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">Description</label>
              <Input value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div className="col-span-12">
              <label className="text-xs text-slate-500">File *</label>
              <Input type="file" accept=".pdf,.docx,.doc,.txt,.md"
                onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !file}>
            {busy ? "Uploading…" : "Upload"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Generic listing
// ---------------------------------------------------------------------------

function LibraryItemRow({ row, downloadUrl, fileName, onDelete, badges }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile(downloadUrl(row.id), fileName?.(row) || row.file_name || row.title);
    } catch {
      toast.error("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card><CardContent className="py-3">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-slate-800 truncate">
            <FileText className="w-3.5 h-3.5 inline mr-1 text-blue-600" />
            {row.title}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {row.client_name || "—"}{row.project_name ? ` · ${row.project_name}` : ""}
            {row.proposal_date ? ` · ${row.proposal_date}` : ""}
            {row.letter_date ? ` · ${row.letter_date}` : ""}
          </p>
          {(badges?.length ?? 0) > 0 && (
            <div className="mt-1 flex gap-1 flex-wrap">
              {badges.map((b, i) => (
                <Badge key={i} variant="outline" className="text-[10px]">{b}</Badge>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-1 shrink-0">
          {row.file_name && (
            <Button size="sm" variant="ghost" onClick={handleDownload} disabled={downloading}
              title={`Download ${row.file_name}`}>
              <Download className="w-3.5 h-3.5" />
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => onDelete(row.id)}>
            <Trash2 className="w-3.5 h-3.5 text-rose-500" />
          </Button>
        </div>
      </div>
    </CardContent></Card>
  );
}


function ProposalsTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await v2Api.library.listProposals() || []); }
    catch { toast.error("Could not load proposals"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!await confirm("Delete this proposal?", { title: "Delete proposal", confirmLabel: "Delete" })) return;
    try { await v2Api.library.deleteProposal(id); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };

  const filtered = q
    ? rows.filter((r) =>
        [r.title, r.client_name, r.project_name, r.industry, r.sector, ...(r.tags || [])]
          .filter(Boolean).some((s) => s.toLowerCase().includes(q.toLowerCase())))
    : rows;

  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title / client / industry / tags…" />
        </div>
        <Button onClick={() => setOpenUpload(true)}>
          <Plus className="w-4 h-4 mr-1" /> Upload proposal
        </Button>
      </div>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <Card><CardContent className="py-8 text-center text-slate-400">
          No past proposals yet. Click <em>Upload proposal</em> to seed the library.
        </CardContent></Card>
      )}
      {filtered.map((r) => (
        <LibraryItemRow
          key={r.id} row={r}
          downloadUrl={v2Api.library.proposalDownloadUrl}
          onDelete={remove}
          badges={[
            r.industry, r.sector,
            r.won === true ? "won" : r.won === false ? "lost" : null,
            ...(r.tags || []),
          ].filter(Boolean)}
        />
      ))}
      <ProposalUploadDialog open={openUpload} onOpenChange={setOpenUpload} onDone={refresh} />
    </div>
  );
}


function CoverLettersTab() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [openUpload, setOpenUpload] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try { setRows(await v2Api.library.listCoverLetters() || []); }
    catch { toast.error("Could not load cover letters"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const remove = async (id) => {
    if (!await confirm("Delete this cover letter?", { title: "Delete cover letter", confirmLabel: "Delete" })) return;
    try { await v2Api.library.deleteCoverLetter(id); refresh(); }
    catch (err) { toast.error(err?.response?.data?.detail ?? "Delete failed"); }
  };

  const filtered = q
    ? rows.filter((r) =>
        [r.title, r.client_name, r.project_name, r.industry, r.sector, r.tone, ...(r.tags || [])]
          .filter(Boolean).some((s) => s.toLowerCase().includes(q.toLowerCase())))
    : rows;

  return (
    <div className="space-y-3">
      <div className="flex justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-2 top-2.5 text-slate-400" />
          <Input className="pl-8" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search title / client / tone / tags…" />
        </div>
        <Button onClick={() => setOpenUpload(true)}>
          <Plus className="w-4 h-4 mr-1" /> Upload cover letter
        </Button>
      </div>
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <Card><CardContent className="py-8 text-center text-slate-400">
          No past cover letters yet.
        </CardContent></Card>
      )}
      {filtered.map((r) => (
        <LibraryItemRow
          key={r.id} row={r}
          downloadUrl={v2Api.library.coverLetterDownloadUrl}
          onDelete={remove}
          badges={[r.tone, r.industry, r.sector, ...(r.tags || [])].filter(Boolean)}
        />
      ))}
      <CoverLetterUploadDialog open={openUpload} onOpenChange={setOpenUpload} onDone={refresh} />
    </div>
  );
}


// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function LibraryPage() {
  const [askOpen, setAskOpen] = useState(false);
  return (
    <div className="p-8 max-w-5xl mx-auto space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" /> Library
        </h1>
        <p className="text-sm text-slate-500">
          Past quotations, proposals and cover letters — tagged for retrieval
          when drafting a new tender response.
        </p>
      </div>
      <Tabs defaultValue="proposals">
        <TabsList>
          <TabsTrigger value="proposals">Proposals</TabsTrigger>
          <TabsTrigger value="cover-letters">Cover letters</TabsTrigger>
          <TabsTrigger value="quotations">Quotations</TabsTrigger>
        </TabsList>
        <TabsContent value="proposals"><ProposalsTab /></TabsContent>
        <TabsContent value="cover-letters"><CoverLettersTab /></TabsContent>
        <TabsContent value="quotations">
          <QuotationsArchivePage />
        </TabsContent>
      </Tabs>

      <AskWidget
        title="Ask library"
        placeholder="Ask about past proposals, exemplars, sectors…"
        examples={[
          "Find a flare monitoring proposal we won.",
          "Show CAAQMS proposals tagged MII.",
          "What technical scope did we promise for NTPC bids?",
        ]}
        askFn={(q, k) => v2Api.library.ask(q, k)}
        onCitationClick={(c) => { if (c.deep_link) window.location.href = c.deep_link; }}
        open={askOpen}
        onOpen={() => setAskOpen(true)}
        onClose={() => setAskOpen(false)}
        buttonColor="bg-indigo-600 hover:bg-indigo-700"
      />
    </div>
  );
}
